import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Upload, X, Plus, Trash2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { CATEGORIES } from "@/data/categories";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";

const initial = {
  name: "", category: "", description: "", short_description: "",
  specs: [], images: [], is_active: true,
};

export default function ProductDialog({ open, onOpenChange, product, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (product) {
        setForm({
          name: product.name || "",
          category: product.category || "",
          description: product.description || "",
          short_description: product.short_description || "",
          specs: product.specs || [],
          images: product.images || [],
          is_active: product.is_active !== false,
        });
      } else {
        setForm(initial);
      }
    }
  }, [open, product]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(res.data);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
      toast.success(`${uploaded.length} imagen(es) subida(s)`);
    } catch (err) {
      toast.error("Error al subir", { description: err?.response?.data?.detail || "" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (id) => {
    setForm((f) => ({ ...f, images: f.images.filter((i) => i.id !== id) }));
  };

  const addSpec = () => setForm((f) => ({ ...f, specs: [...f.specs, { key: "", value: "" }] }));
  const updateSpec = (idx, field, value) => setForm((f) => ({
    ...f,
    specs: f.specs.map((s, i) => i === idx ? { ...s, [field]: value } : s),
  }));
  const removeSpec = (idx) => setForm((f) => ({ ...f, specs: f.specs.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category) {
      toast.error("Completa los campos requeridos");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        description: form.description,
        short_description: form.short_description,
        specs: form.specs.filter((s) => s.key && s.value),
        images: form.images,
        is_active: form.is_active,
      };
      if (product) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Producto creado");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error("Error al guardar", { description: err?.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full border border-slate-300 rounded-none px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white";
  const labelCls = "block text-xs font-display font-bold text-slate-700 mb-1.5 uppercase tracking-wider";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-none border-2 border-slate-950 max-h-[92vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-amber-600">
            {product ? "Editar producto" : "Nuevo producto"}
          </div>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">
            {product ? form.name : "Crear producto"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={save} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className={labelCls}>Nombre *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="product-name-input" />
            </div>
            <div>
              <label className={labelCls}>Categoría *</label>
              <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="product-category-select">
                <option value="">Seleccionar...</option>
                {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Estado</label>
              <select value={form.is_active ? "1" : "0"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })} className={inputCls} data-testid="product-active-select">
                <option value="1">Activo (visible)</option>
                <option value="0">Oculto</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Descripción corta</label>
              <input value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className={inputCls} placeholder="Frase breve que aparece en el catálogo" data-testid="product-short-input" />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Descripción completa</label>
              <RichTextEditor
                value={form.description}
                onChange={(html) => setForm({ ...form, description: html })}
                placeholder="Describe el equipo: aplicaciones, ventajas, materiales..."
              />
              <p className="text-[10px] font-mono text-slate-400 mt-1 uppercase tracking-wider">
                Editor enriquecido: negritas, listas, alineación, colores, tamaño e interlineado.
              </p>
            </div>
          </div>

          {/* Images */}
          <div>
            <label className={labelCls}>Imágenes</label>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {form.images.map((img) => (
                <div key={img.id} className="relative aspect-square border border-slate-300 bg-slate-100 overflow-hidden group">
                  <img src={fileUrl(img.storage_path)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(img.id)} data-testid={`remove-image-${img.id}`} className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-image-btn" className="aspect-square border-2 border-dashed border-slate-300 hover:border-amber-500 hover:bg-amber-50 flex flex-col items-center justify-center gap-1 text-slate-500 text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                {uploading ? "Subiendo..." : "Agregar"}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="file-input" />
            </div>
          </div>

          {/* Specs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls + " mb-0"}>Especificaciones técnicas</label>
              <button type="button" onClick={addSpec} data-testid="add-spec-btn" className="text-xs font-display font-bold uppercase tracking-wider text-amber-600 hover:text-amber-700 inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Agregar
              </button>
            </div>
            <div className="space-y-2">
              {form.specs.length === 0 && (
                <p className="text-xs text-slate-400 font-mono">Sin especificaciones. Agrega pares clave-valor para mostrar una ficha técnica.</p>
              )}
              {form.specs.map((s, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2">
                  <input value={s.key} onChange={(e) => updateSpec(idx, "key", e.target.value)} placeholder="Capacidad" className={inputCls + " col-span-5"} data-testid={`spec-key-${idx}`} />
                  <input value={s.value} onChange={(e) => updateSpec(idx, "value", e.target.value)} placeholder="100 ton/h" className={inputCls + " col-span-6"} data-testid={`spec-value-${idx}`} />
                  <button type="button" onClick={() => removeSpec(idx)} data-testid={`remove-spec-${idx}`} className="col-span-1 border border-slate-300 hover:bg-red-50 hover:border-red-600 hover:text-red-600 flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={() => onOpenChange(false)} className="px-5 py-3 font-display font-bold uppercase text-xs tracking-wider text-slate-600 hover:text-slate-950" data-testid="product-cancel-btn">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-6 py-3 text-sm btn-brutal disabled:opacity-60 inline-flex items-center gap-2" data-testid="product-save-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {product ? "Guardar cambios" : "Crear producto"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
